// Admin-only data source switch: "live" (default) or "frozen" (the May 21
// freeze snapshot, served by the frozenApi Cloud Function from a separate
// Firestore database; see functions/utils/frozenTarget.js).
//
// Live odds currently come from a sandbox OpticOdds key (every price +100), so
// admins flip to Frozen to check EV math on real prices. The choice is stored
// per browser and only takes effect for admins; frozenApi itself also rejects
// non-admins.

export const DATA_SOURCE_KEY = 'admin_data_source';
export const FROZEN_LABEL = 'Frozen · May 21';

// Must match FROZEN_ROUTES in functions/utils/frozenTarget.js.
export const FROZEN_ROUTES = Object.freeze([
  'nlOdds',
  'queryCachedFixtures',
  'queryCachedSGPs',
  'getCachedSGPs',
  'getTickerFixtures',
  'getAllCachedFixtures',
  'getCachedOdds',
]);

// Browser caches holding data from one source; dropped on every switch.
const SOURCE_SCOPED_CACHE_KEYS = ['tickerFixturesCache', 'tickerFixturesCache:frozen', 'sgpCacheStatus', 'sgpCacheVerifiedAt'];

// While Frozen is stored but admin status hasn't loaded yet, requests wait
// (up to this long) so a reload never fires live searches under the badge.
export const ADMIN_STATUS_WAIT_MS = 15000;

let _isAdmin = false;
let _getIdToken = null;
let _statusKnown = false;
let _resolveStatus = () => {};
let _statusPromise = new Promise(r => { _resolveStatus = r; });

/**
 * Called by FrozenDataBadge on every render. `known` = admin status is settled
 * (signed out, or the user doc has loaded). Once known and not an admin, any
 * stored Frozen choice and frozen caches are wiped (shared browser, sign-out).
 */
export function configureDataSource({ isAdmin = false, known = true, getIdToken = null } = {}) {
  _isAdmin = !!isAdmin;
  _getIdToken = typeof getIdToken === 'function' ? getIdToken : null;
  if (!known) return;
  if (!_isAdmin && getStoredDataSource() === 'frozen') setStoredDataSource('live');
  if (!_statusKnown) { _statusKnown = true; _resolveStatus(); }
}

/** Test hook: back to "status not loaded yet". */
export function _resetDataSourceStatusForTests() {
  _statusKnown = false;
  _statusPromise = new Promise(r => { _resolveStatus = r; });
}

async function waitForAdminStatus() {
  if (_statusKnown || getStoredDataSource() !== 'frozen') return;
  let timer;
  await Promise.race([_statusPromise, new Promise(r => { timer = setTimeout(r, ADMIN_STATUS_WAIT_MS); })]);
  clearTimeout(timer);
}

/** localStorage key for a per-source cache ("tickerFixturesCache" / "…:frozen"). */
export function sourceCacheKey(base) {
  return getStoredDataSource() === 'frozen' ? `${base}:frozen` : base;
}

function storage() {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null;
  } catch {
    return null;
  }
}

export function getStoredDataSource() {
  try {
    return storage()?.getItem(DATA_SOURCE_KEY) === 'frozen' ? 'frozen' : 'live';
  } catch {
    return 'live';
  }
}

export function isFrozenDataActive() {
  return _isAdmin && getStoredDataSource() === 'frozen';
}

/** Persist the choice and drop caches filled from the other source. */
export function setStoredDataSource(source) {
  const s = storage();
  if (!s) return;
  try {
    if (source === 'frozen') s.setItem(DATA_SOURCE_KEY, 'frozen');
    else s.removeItem(DATA_SOURCE_KEY);
    for (const k of SOURCE_SCOPED_CACHE_KEYS) s.removeItem(k);
  } catch {
    // Storage blocked: the switch simply doesn't stick.
  }
}

function routeName(path) {
  return String(path || '').replace(/^\/+/, '').split(/[/?]/)[0];
}

/** URL for `path` ("nlOdds" or "getCachedSGPs?fixture_id=1") under `base`. */
export function dataEndpointUrl(base, path, frozen = isFrozenDataActive()) {
  const clean = String(path || '').replace(/^\/+/, '');
  if (frozen && FROZEN_ROUTES.includes(routeName(clean))) return `${base}/frozenApi/${clean}`;
  return `${base}/${clean}`;
}

// Live routes that accept a signed-in user's ID token so the server can attribute
// the search (query_analytics.userId). nlOdds is the only one whose CORS allows it.
const LIVE_IDENTITY_ROUTES = ['nlOdds'];
// A token refresh near expiry is a network call; a search never waits longer than this for it.
export const LIVE_IDENTITY_WAIT_MS = 400;

/** Adds the signed-in user's bearer token on identity routes; never blocks the request. */
async function withLiveIdentity(path, init) {
  if (!_getIdToken || !LIVE_IDENTITY_ROUTES.includes(routeName(path))) return init;
  let token = null;
  let timer;
  try {
    token = await Promise.race([
      Promise.resolve().then(() => _getIdToken()),
      new Promise(r => { timer = setTimeout(() => r(null), LIVE_IDENTITY_WAIT_MS); }),
    ]);
  } catch { token = null; } finally { clearTimeout(timer); }
  if (!token) return init;
  return { ...init, headers: { ...(init.headers || {}), Authorization: `Bearer ${token}` } };
}

/**
 * { url, init } for a data request. When Frozen is active and the route is
 * frozen-capable, the URL goes through frozenApi and carries the admin's ID token.
 */
export async function dataRequest(base, path, init = {}) {
  await waitForAdminStatus();
  const frozen = isFrozenDataActive() && FROZEN_ROUTES.includes(routeName(path));
  const url = dataEndpointUrl(base, path, frozen);
  if (!frozen) return { url, init: await withLiveIdentity(path, init) };
  const token = _getIdToken ? await _getIdToken() : null;
  if (!token) throw new Error('Frozen data needs a signed-in admin');
  return { url, init: { ...init, headers: { ...(init.headers || {}), Authorization: `Bearer ${token}` } } };
}

/** fetch() through dataRequest. */
export async function dataFetch(base, path, init = {}) {
  const req = await dataRequest(base, path, init);
  return fetch(req.url, req.init);
}
